import React, { useContext, useEffect, useState } from "react";
import styles from "./KonkurBooklets.module.css";
import Spinner from "../../../Utils/ColoredSpinner/Spinner";
import { UserTokenContext } from "../../../Contexts/userToken";
import { getCurrentUserDataService } from "../../../Services/Profile/get/getCurrentUserDataService";
import { getKonkurYearsService } from "../../../Services/KonkurBooklets/get/getKonkurYearsService";
import {
  I_KonkurBookletCategory,
  I_KonkurBookletPdf,
  I_KonkurBookletYear,
} from "../../../Models/interfaces";
import { getKonkurBookletCategoriesService } from "../../../Services/KonkurBooklets/get/getKonkurCategoriesService";
import { getKonkurBookletPdfsService } from "../../../Services/KonkurBooklets/get/getKonkurPdfsService";
import YearItem from "./components/YearItem";
import PurchaseModal from "./components/PurchaseModal";
import { downloadPdf } from "../../../Utils/downloader";

interface KonkurBookletsProps {
  setActiveGradeName: React.Dispatch<React.SetStateAction<string>>;
}

const KonkurBooklets: React.FC<KonkurBookletsProps> = ({ setActiveGradeName }) => {
  const userToken = useContext(UserTokenContext);

  const [gradeId, setGradeId] = useState<string | number | null>(null);
  const [years, setYears] = useState<I_KonkurBookletYear[]>([]);

  const [yearCategoriesMap, setYearCategoriesMap] = useState<
    Record<number, I_KonkurBookletCategory[]>
  >({});

  const [categoryPdfsMap, setCategoryPdfsMap] = useState<
    Record<number, I_KonkurBookletPdf[]>
  >({});

  const [expandedYears, setExpandedYears] = useState<Set<number>>(new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(
    new Set(),
  );

  const [loadingYears, setLoadingYears] = useState<boolean>(false);
  const [loadingCategories, setLoadingCategories] = useState<
    Record<number, boolean>
  >({});
  const [loadingPdfs, setLoadingPdfs] = useState<Record<number, boolean>>({});

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [forPurchasePdf, setForPurchasePdf] =
    useState<I_KonkurBookletPdf | null>(null);

  // دریافت اطلاعات کاربر
  useEffect(() => {
    if (!userToken) return;
    getCurrentUserDataService(userToken)
      .then(({ data }) => {
        const g = data?.data?.grade_id ?? data?.grade_id ?? null;
        if (g) {
          setGradeId(g);
          setActiveGradeName(data?.data?.grade?.title ?? data?.grade?.title ?? "");
        }
      })
      .catch((err) => console.error(err));
  }, [userToken]);

  // دریافت لیست سال‌ها
  useEffect(() => {
    if (!gradeId || !userToken) return;
    setLoadingYears(true);
    getKonkurYearsService(userToken)
      .then(({ data }) => {
        setYears(data?.data ?? []);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoadingYears(false));
  }, [gradeId, userToken]);

  const toggleYear = async (yearId: number) => {
    const newExpanded = new Set(expandedYears);
    if (newExpanded.has(yearId)) {
      newExpanded.delete(yearId);
      setExpandedYears(newExpanded);
      return;
    }

    newExpanded.add(yearId);
    setExpandedYears(newExpanded);

    if (!yearCategoriesMap[yearId] && userToken) {
      setLoadingCategories((prev) => ({ ...prev, [yearId]: true }));
      try {
        const res = await getKonkurBookletCategoriesService(userToken, yearId);
        const cats = res?.data?.data ?? [];
        setYearCategoriesMap((prev) => ({ ...prev, [yearId]: cats }));
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingCategories((prev) => ({ ...prev, [yearId]: false }));
      }
    }
  };

  const toggleCategory = async (categoryId: number) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
      setExpandedCategories(newExpanded);
      return;
    }

    newExpanded.add(categoryId);
    setExpandedCategories(newExpanded);

    if (!categoryPdfsMap[categoryId] && userToken) {
      setLoadingPdfs((prev) => ({ ...prev, [categoryId]: true }));
      try {
        const res = await getKonkurBookletPdfsService(userToken, {
          categoryId,
        });
        const pdfs = res?.data?.data ?? [];
        setCategoryPdfsMap((prev) => ({ ...prev, [categoryId]: pdfs }));
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingPdfs((prev) => ({ ...prev, [categoryId]: false }));
      }
    }
  };

  const handleDownload = async (pdf: I_KonkurBookletPdf) => {
    if (pdf.download_url) {
      try {
        await downloadPdf(pdf.download_url.replace("http://easyno.ir/", "https://app.amoozeshmelli.com/dfiles/"), pdf.title + ".pdf");
      } catch (err) {
        alert("خطا در دانلود فایل:‌ " + err);
      }
    } else {
      alert("لینک دانلود موجود نیست.");
    }
  };

  const handlePurchase = (pdf: I_KonkurBookletPdf) => {
    setForPurchasePdf(pdf);
    setIsModalOpen(true);
  };

  return (
    <div className={`${styles.quizCreator} mx-8 p-4 bg-white dark:!text-black`}>
      <div className="p-6 rounded-lg border border-gray-200 bg-gray-50">
        <h2 className="text-xl font-bold mb-4">دفترچه‌های کنکور</h2>

        {loadingYears ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : years.length === 0 ? (
          <p className="text-gray-500">هیچ سالی یافت نشد.</p>
        ) : (
          <div className="space-y-2 [direction:rtl]">
            {years.map((year) => (
              <YearItem
                key={year.id}
                year={year}
                isExpanded={expandedYears.has(year.id)}
                onToggle={toggleYear}
                categories={yearCategoriesMap[year.id] || []}
                loading={loadingCategories[year.id] || false}
                expandedCategories={expandedCategories}
                onToggleCategory={toggleCategory}
                categoryPdfsMap={categoryPdfsMap}
                loadingPdfs={loadingPdfs}
                onDownload={handleDownload}
                onPurchase={handlePurchase}
              />
            ))}
          </div>
        )}
      </div>

      {isModalOpen ? (
        <PurchaseModal
          setIsModalOpen={setIsModalOpen}
          categoryId={forPurchasePdf?.konkur_booklet_category_id ?? 0}
          pdfItemId={forPurchasePdf?.id ?? 0}
          userToken={userToken}
          isPaid={forPurchasePdf?.has_paid ?? false}
          productName={forPurchasePdf?.title ?? ""}
          productPrice={forPurchasePdf?.price ?? 0}
          setCategoryPdfsMap={setCategoryPdfsMap}
        />
      ) : null}
    </div>
  );
};

export default KonkurBooklets;
